import React, { useEffect } from 'react'
import { Card, makeStyles, Grid, InputField, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import {
  TagSelection,
  useClientOptions,
  useUser,
  PhoneNumberInput,
  PhoneNumberExtensionInput
} from '@paintscout/ui-business'
import { useSnackbar } from 'notistack'
import classnames from 'classnames'
import type { Company } from '@paintscout/api'
import { getFeatures, getQuoteTags, setOwner } from '@paintscout/util/builder'
import { isEmailValid, validEmailRegExp } from '@paintscout/util'
import { useFlags } from 'shared/components/common/FlagsProvider'
import type { QuoteTag } from 'paintscout'
import LeadSourceSelector from '../dialogs/ContactDialog/LeadSourceSelector'
import { useContactAddressSearch } from './useContactAddressSearch'
import { useInitialOpenLoad } from './useInitialOpenLoad'
import { useCompanyValues } from './useCompanyValues'

type FormProps = JSX.IntrinsicElements['form']

export interface CompanyFormProps extends Omit<FormProps, 'onSubmit'> {
  classes?: StyleClasses<typeof useStyles>
  company?: Partial<Company>
  disabled?: boolean
  disableCompanySelector?: boolean
  isSecondary?: boolean

  onSubmit: (company: Company) => any
  onDirtyChange?: (dirty: boolean) => void
}

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {},
    selectField: { marginTop: theme.spacing(), paddingBottom: theme.spacing(0.5) },
    dropdownRelative: {
      position: 'relative',
      cursor: 'pointer'
    },
    dropdown: {
      zIndex: 21474899999,
      position: 'absolute',
      boxShadow: theme.shadows[2],
      width: '98%'
    },
    dropdownItem: {
      padding: theme.spacing(1, 2),
      '&:hover': {
        outline: '1px solid #e0e0e0',
        backgroundColor: theme.palette.action.hover
      }
    },
    popper: {
      marginBottom: `-${theme.spacing(7.5)}px`
    }
  }),
  { name: 'CompanyForm' }
)

function CompanyForm({ company, disabled, onSubmit, onDirtyChange, ...baseProps }: CompanyFormProps) {
  const classes = useStyles({})
  const { isInitialLoad, isOpen, setIsOpen } = useInitialOpenLoad({
    initialLoad: true,
    initialOpen: false
  })
  const { options } = useClientOptions()
  const { crm: crmFlag } = useFlags()
  const features = getFeatures({ options })
  const hasTags = !!(features.quotes?.tags?.enabled || crmFlag)
  const hasSources = crmFlag
  const { user, isSuperadmin, isSupport } = useUser()
  const {
    companyValues,
    handleFieldChange,
    mapAddressToValues,
    companySources,
    handleSourceChange,
    handleTagChange,
    isDirty
  } = useCompanyValues({
    company,
    options
  })

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])
  const internationalMode = options?.options?.internationalMode ?? false
  const { enqueueSnackbar } = useSnackbar()
  const quoteTags = getQuoteTags({ options })

  const { addressSearchResults, debouncedAddressSearch } = useContactAddressSearch({
    options,
    isInitialLoad
  })

  async function handleSubmit(event: React.FormEvent<HTMLElement>) {
    event.preventDefault()

    if (!!companyValues.email?.trim() && !isEmailValid(companyValues.email)) {
      enqueueSnackbar('Please enter a valid email address', { variant: 'error' })
      return
    }

    // // merge companyValues and omit falsey companyValues
    let newCompany: Company = {
      ...companyValues,
      updated: Date.now()
    }

    if (!newCompany.owner && !isSuperadmin && !isSupport) {
      newCompany = setOwner({ doc: newCompany, user })
    }

    Object.keys(newCompany).forEach((key) => {
      if (typeof newCompany[key] !== 'string') {
        return
      }

      newCompany[key] = newCompany[key].trim()
    })

    onSubmit(newCompany)
  }

  const commonInputProps = {
    onChange: handleFieldChange,
    margin: 'dense' as const,
    fullWidth: true,
    disabled,
    classes: { root: classes.textField }
  }

  return (
    <form className={classes.root} autoComplete="off" onSubmit={handleSubmit} {...baseProps}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={hasSources ? 6 : 12} md={hasSources ? 6 : 12}>
          <InputField
            {...commonInputProps}
            required={true}
            error={!companyValues.name}
            pattern={{ expression: '(.*[A-Za-z]+.*)', title: 'Name' }}
            id="name"
            label="Name"
            autoComplete={'off'}
            value={companyValues.name}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <InputField
            {...commonInputProps}
            pattern={{ expression: validEmailRegExp.source, title: 'Email Address' }}
            id="email"
            label="Email"
            value={companyValues.email}
            autoComplete={'off'}
          />
        </Grid>
        <Grid item xs={8} sm={4} md={4}>
          <InputField
            {...commonInputProps}
            inputComponent={PhoneNumberInput}
            inputProps={{
              separator: options?.options?.phoneNumbers?.separator,
              role: 'presentation'
            }}
            autoComplete={'off'}
            id="phone_number"
            label="Phone"
            value={companyValues.phone_number}
          />
        </Grid>
        <Grid item xs={4} sm={2} md={2}>
          <InputField
            {...commonInputProps}
            inputComponent={PhoneNumberExtensionInput}
            autoComplete={'off'}
            id="phone_ext"
            label="Ext."
            value={companyValues.phone_ext}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} className={classes.dropdownRelative}>
          <InputField
            {...commonInputProps}
            autoComplete={'off'}
            inputProps={{
              role: 'presentation'
            }}
            onChange={(e) => {
              handleFieldChange(e)
              if (e.target.value.length > 0) {
                setIsOpen(true)
                debouncedAddressSearch(e.target.value)
              }
            }}
            id="address"
            label={'Address'}
            fullWidth={true}
            value={companyValues.address}
            onClick={() => setIsOpen(true)}
            onBlur={() => setIsOpen(false)}
          />
          {addressSearchResults.length > 0 && isOpen && (
            <Card className={classes.dropdown}>
              {addressSearchResults.map((result, i) => {
                const label = [result.address, result.municipality].filter((item) => item).join(', ')
                const label2 = [result.region, result.postalCode].filter((item) => item).join(', ')

                return (
                  <div
                    key={i}
                    onMouseDown={() => {
                      setIsOpen(false)
                      mapAddressToValues(result)
                    }}
                    className={classes.dropdownItem}
                  >
                    <Typography variant={'body1'}>{label}</Typography>
                    <Typography variant={'subtitle2'}>{label2}</Typography>
                  </div>
                )
              })}
            </Card>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <InputField
            {...commonInputProps}
            autoComplete={'off'}
            id="city"
            inputProps={{
              role: 'presentation'
            }}
            label="City"
            value={companyValues.city}
            onChange={handleFieldChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <InputField
            {...commonInputProps}
            classes={{
              root: classnames(classes.textField, classes.provinceField)
            }}
            autoComplete={'off'}
            id="province"
            label="State / Province"
            inputProps={internationalMode ? {} : { maxLength: 2, role: 'presentation' }}
            value={companyValues.province}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <InputField
            {...commonInputProps}
            autoComplete={'off'}
            inputProps={{
              role: 'presentation'
            }}
            id="postal"
            label="Zip / Postal Code"
            value={companyValues.postal}
          />
        </Grid>
        {hasSources && (
          <Grid item xs={12} sm={6} md={6}>
            <LeadSourceSelector
              classes={{ root: classes.selectField }}
              disabled={false}
              value={{
                value: companyValues.lead_source ? companyValues.lead_source : '',
                label: companyValues.lead_source
                  ? companySources.find(
                      (source) =>
                        source.label.trim() === companyValues.lead_source.trim() ||
                        source.name === companyValues.lead_source.trim()
                    )?.label
                  : ''
              }}
              sources={companySources.map((source) => ({
                value: source.name,
                label: source.label
              }))}
              onChange={handleSourceChange}
            />
          </Grid>
        )}
        {hasTags && (
          <Grid item xs={12}>
            <TagSelection
              label="Tags"
              classes={{ root: classes.selectField, popper: classes.popper }}
              popperPlacement="top"
              appliedTags={
                companyValues?.tags?.map((tag: string) => {
                  // grab the right tag from quoteTags
                  const quoteTag = quoteTags.find((quoteTag) => quoteTag.name === tag)
                  return quoteTag
                }) || []
              }
              fullWidth
              handleChange={(updatedTags: QuoteTag[]) => {
                handleTagChange(updatedTags)
              }}
            />
          </Grid>
        )}
      </Grid>
    </form>
  )
}

export default CompanyForm
